/**
 * assets/product-delivery-eta.js
 * Dynamic variant-level delivery time & working-day ETA calculator for michelangelo intimo.
 * Supports:
 * - Instant 0ms updates on variant selection (color/size/sale-chip) & quantity changes (`input[name="quantity"]`)
 * - Automatic supplier fallback when selected quantity exceeds available stock (`qty > inv` & `policy === 'continue'`)
 * - Working-day date calculation (skipping weekends & Swiss public holidays)
 * - Human-readable date formatting ("Freitag, 23. Oktober") in DE, FR, IT, EN
 * - Line item property (`properties[Lieferung]`) carried through Cart, Checkout & Order Confirmation
 * - Scheduled Global Vacation Mode (Europe/Zurich timezone)
 */
(function () {
  if (window.__miDeliveryEtaInitialized) return;
  window.__miDeliveryEtaInitialized = true;

  const TIERS = {
    'ab Lager, 1-2 Arbeitstage': {
      minDays: 1,
      maxDays: 2,
      status: 'fast',
      label: {
        de: 'Lieferfrist: ab Lager, 1–2 Arbeitstage',
        fr: 'Délai de livraison : en stock, 1–2 jours ouvrés',
        it: 'Tempi di consegna: disponibile a magazzino, 1–2 giorni lavorativi',
        en: 'Delivery time: in stock, 1–2 working days'
      },
      short: {
        de: 'ca. 1–2 Arbeitstage',
        fr: 'env. 1–2 jours ouvrés',
        it: 'ca. 1–2 giorni lavorativi',
        en: 'approx. 1–2 working days'
      }
    },
    'kurz, 2-4 Arbeitstage': {
      minDays: 2,
      maxDays: 4,
      status: 'fast',
      label: {
        de: 'Lieferfrist: kurz, 2–4 Arbeitstage',
        fr: 'Délai de livraison : court, 2–4 jours ouvrés',
        it: 'Tempi di consegna: breve, 2–4 giorni lavorativi',
        en: 'Delivery time: short, 2–4 working days'
      },
      short: {
        de: 'ca. 2–4 Arbeitstage',
        fr: 'env. 2–4 jours ouvrés',
        it: 'ca. 2–4 giorni lavorativi',
        en: 'approx. 2–4 working days'
      }
    },
    '5-7 Arbeitstage': {
      minDays: 5,
      maxDays: 7,
      status: 'medium',
      label: {
        de: 'Lieferfrist: 5–7 Arbeitstage',
        fr: 'Délai de livraison : 5–7 jours ouvrés',
        it: 'Tempi di consegna: 5–7 giorni lavorativi',
        en: 'Delivery time: 5–7 working days'
      },
      short: {
        de: 'ca. 5–7 Arbeitstage',
        fr: 'env. 5–7 jours ouvrés',
        it: 'ca. 5–7 giorni lavorativi',
        en: 'approx. 5–7 working days'
      }
    },
    '7-10 Arbeitstage': {
      minDays: 7,
      maxDays: 10,
      status: 'medium',
      label: {
        de: 'Lieferfrist: 7–10 Arbeitstage',
        fr: 'Délai de livraison : 7–10 jours ouvrés',
        it: 'Tempi di consegna: 7–10 giorni lavorativi',
        en: 'Delivery time: 7–10 working days'
      },
      short: {
        de: 'ca. 7–10 Arbeitstage',
        fr: 'env. 7–10 jours ouvrés',
        it: 'ca. 7–10 giorni lavorativi',
        en: 'approx. 7–10 working days'
      }
    },
    'rund 2 Wochen': {
      minDays: 9,
      maxDays: 12,
      status: 'slow',
      label: {
        de: 'Lieferfrist: rund 2 Wochen',
        fr: 'Délai de livraison : env. 2 semaines',
        it: 'Tempi di consegna: ca. 2 settimane',
        en: 'Delivery time: approx. 2 weeks'
      },
      short: {
        de: 'rund 2 Wochen',
        fr: 'env. 2 semaines',
        it: 'ca. 2 settimane',
        en: 'approx. 2 weeks'
      }
    },
    'länger als 2 Wochen': {
      minDays: 12,
      maxDays: 18,
      openEnded: true,
      status: 'slow',
      label: {
        de: 'Lieferfrist: länger als 2 Wochen',
        fr: 'Délai de livraison : plus de 2 semaines',
        it: 'Tempi di consegna: oltre 2 settimane',
        en: 'Delivery time: more than 2 weeks'
      },
      short: {
        de: 'länger als 2 Wochen',
        fr: 'plus de 2 semaines',
        it: 'oltre 2 settimane',
        en: 'more than 2 weeks'
      }
    },
    'auf Anfrage': {
      minDays: 20,
      maxDays: 30,
      calendarDays: 28,
      onRequest: true,
      status: 'slow',
      label: {
        de: 'Lieferfrist: auf Anfrage',
        fr: 'Délai de livraison : sur demande',
        it: 'Tempi di consegna: su richiesta',
        en: 'Delivery time: on request'
      },
      short: {
        de: 'auf Anfrage',
        fr: 'sur demande',
        it: 'su richiesta',
        en: 'on request'
      }
    }
  };

  function resolveTierKey(rawLf) {
    if (!rawLf) return '5-7 Arbeitstage';
    const s = String(rawLf).trim();
    if (TIERS[s]) return s;
    const lower = s.toLowerCase();
    if (lower.includes('anfrage') || lower.includes('demande') || lower.includes('richiesta') || lower.includes('request')) {
      return 'auf Anfrage';
    }
    if (lower.includes('1-2') || lower.includes('1–2') || lower.includes('lager') || lower.includes('stock') || lower.includes('magazzino')) {
      return 'ab Lager, 1-2 Arbeitstage';
    }
    if (lower.includes('2-4') || lower.includes('2–4') || lower.includes('kurz') || lower.includes('court') || lower.includes('breve') || lower.includes('short')) {
      return 'kurz, 2-4 Arbeitstage';
    }
    if (lower.includes('5-7') || lower.includes('5–7')) return '5-7 Arbeitstage';
    if (lower.includes('7-10') || lower.includes('7–10')) return '7-10 Arbeitstage';
    if (lower.includes('länger') || lower.includes('more than') || lower.includes('plus de') || lower.includes('oltre')) {
      return 'länger als 2 Wochen';
    }
    if (lower.includes('2 wochen') || lower.includes('2 weeks') || lower.includes('2 semaines') || lower.includes('2 settimane')) {
      return 'rund 2 Wochen';
    }
    return '5-7 Arbeitstage';
  }

  function getZurichNowParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Zurich',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());
    const map = {};
    parts.forEach(p => { map[p.type] = p.value; });
    const hr = map.hour === '24' ? '00' : map.hour;
    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      isoString: `${map.year}-${map.month}-${map.day}T${hr}:${map.minute}`
    };
  }

  function parseDateInput(dateStr, timeStr, defaultTime) {
    if (!dateStr) return null;
    const clean = dateStr.trim();
    let y, m, d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
      const sp = clean.split('-');
      y = parseInt(sp[0], 10);
      m = parseInt(sp[1], 10);
      d = parseInt(sp[2], 10);
    } else if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(clean)) {
      const sp = clean.split('.');
      d = parseInt(sp[0], 10);
      m = parseInt(sp[1], 10);
      y = parseInt(sp[2], 10);
    } else {
      return null;
    }
    const tClean = (timeStr && /^\d{1,2}:\d{2}$/.test(timeStr.trim())) ? timeStr.trim() : (defaultTime || '00:00');
    const [hh, mm] = tClean.split(':').map(n => String(parseInt(n, 10)).padStart(2, '0'));
    const mmStr = String(m).padStart(2, '0');
    const ddStr = String(d).padStart(2, '0');
    return {
      year: y,
      month: m,
      day: d,
      isoString: `${y}-${mmStr}-${ddStr}T${hh}:${mm}`,
      dateObj: new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
    };
  }

  function isSwissPublicHoliday(dateUtc) {
    const m = dateUtc.getUTCMonth() + 1;
    const d = dateUtc.getUTCDate();
    if (m === 1 && (d === 1 || d === 2)) return true;
    if (m === 8 && d === 1) return true;
    if (m === 12 && (d === 25 || d === 26)) return true;
    return false;
  }

  function addWorkingDays(startDateUtc, workingDaysToAdd) {
    const current = new Date(startDateUtc.getTime());
    let added = 0;
    while (added < workingDaysToAdd) {
      current.setUTCDate(current.getUTCDate() + 1);
      const dow = current.getUTCDay();
      if (dow !== 0 && dow !== 6 && !isSwissPublicHoliday(current)) {
        added++;
      }
    }
    return current;
  }

  function addCalendarDaysToNextWorkingDay(startDateUtc, calendarDaysToAdd) {
    const current = new Date(startDateUtc.getTime());
    current.setUTCDate(current.getUTCDate() + calendarDaysToAdd);
    while (current.getUTCDay() === 0 || current.getUTCDay() === 6 || isSwissPublicHoliday(current)) {
      current.setUTCDate(current.getUTCDate() + 1);
    }
    return current;
  }

  function formatHumanDate(dateUtc, lang) {
    const localeMap = {
      de: 'de-CH',
      fr: 'fr-CH',
      it: 'it-CH',
      en: 'en-GB'
    };
    const loc = localeMap[lang] || 'de-CH';
    return new Intl.DateTimeFormat(loc, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC'
    }).format(dateUtc);
  }

  function isVacationActive(container) {
    const enabled = container.getAttribute('data-vac-enabled') === 'true';
    if (!enabled) return { active: false };

    const now = getZurichNowParts();
    const startParsed = parseDateInput(container.getAttribute('data-vac-start-date'), container.getAttribute('data-vac-start-time'), '00:00');
    const endParsed = parseDateInput(container.getAttribute('data-vac-end-date'), container.getAttribute('data-vac-end-time'), '23:59');
    const shipParsed = parseDateInput(container.getAttribute('data-vac-ship-date'), '12:00', '12:00');

    if (startParsed && now.isoString < startParsed.isoString) {
      return { active: false };
    }
    if (endParsed && now.isoString > endParsed.isoString) {
      return { active: false };
    }

    let baseShipDate = shipParsed ? shipParsed.dateObj : null;
    if (!baseShipDate && endParsed) {
      baseShipDate = addWorkingDays(endParsed.dateObj, 1);
    }
    if (!baseShipDate) {
      baseShipDate = new Date(Date.UTC(now.year, now.month - 1, now.day, 12, 0, 0));
    }

    return {
      active: true,
      shipDate: baseShipDate
    };
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function localizePreorderText(rawText, lang) {
    if (!rawText) return '';
    let t = String(rawText).trim();
    if (lang === 'de') return t;

    if (lang === 'fr') {
      t = t.replace(/^Voraussichtlicher\s+Liefertermin\s*:\s*/i, 'Date de livraison prévue : ');
      t = t.replace(/\bAnfang\b/g, 'début').replace(/\bMitte\b/g, 'mi-').replace(/\bEnde\b/g, 'fin');
      const monthsFr = {
        Januar: 'janvier', Februar: 'février', März: 'mars', April: 'avril',
        Mai: 'mai', Juni: 'juin', Juli: 'juillet', August: 'août',
        September: 'septembre', Oktober: 'octobre', November: 'novembre', Dezember: 'décembre'
      };
      for (const [deM, frM] of Object.entries(monthsFr)) {
        t = t.replace(new RegExp('\\b' + deM + '\\b', 'g'), frM);
      }
      t = t.replace(/mi-\s+/g, 'mi-');
    } else if (lang === 'it') {
      t = t.replace(/^Voraussichtlicher\s+Liefertermin\s*:\s*/i, 'Data di consegna prevista: ');
      t = t.replace(/\bAnfang\b/g, 'inizio').replace(/\bMitte\b/g, 'metà').replace(/\bEnde\b/g, 'fine');
      const monthsIt = {
        Januar: 'gennaio', Februar: 'febbraio', März: 'marzo', April: 'aprile',
        Mai: 'maggio', Juni: 'giugno', Juli: 'luglio', August: 'agosto',
        September: 'settembre', Oktober: 'ottobre', November: 'novembre', Dezember: 'dicembre'
      };
      for (const [deM, itM] of Object.entries(monthsIt)) {
        t = t.replace(new RegExp('\\b' + deM + '\\b', 'g'), itM);
      }
    } else if (lang === 'en') {
      t = t.replace(/^Voraussichtlicher\s+Liefertermin\s*:\s*/i, 'Expected delivery: ');
      t = t.replace(/\bAnfang\b/g, 'early').replace(/\bMitte\b/g, 'mid').replace(/\bEnde\b/g, 'end of');
      const monthsEn = {
        Januar: 'January', Februar: 'February', März: 'March', April: 'April',
        Mai: 'May', Juni: 'June', Juli: 'July', August: 'August',
        September: 'September', Oktober: 'October', November: 'November', Dezember: 'December'
      };
      for (const [deM, enM] of Object.entries(monthsEn)) {
        t = t.replace(new RegExp('\\b' + deM + '\\b', 'g'), enM);
      }
    }
    return t;
  }

  function formatLineItemPropertyValue(minDateStr, maxDateStr, lang, isOpenEnded, isOnRequest) {
    if (isOnRequest) {
      if (lang === 'fr') return `au plus tôt à partir du ${minDateStr}, nous vous informerons après réception de la commande`;
      if (lang === 'it') return `al più presto a partire da ${minDateStr}, La informeremo dopo la ricezione dell'ordine`;
      if (lang === 'en') return `earliest from ${minDateStr}, we will inform you after receipt of order`;
      return `frühestens ab dem ${minDateStr}, wir informieren Sie nach Bestellungseingang`;
    }
    if (isOpenEnded) {
      if (lang === 'fr') return `au plus tôt à partir du ${minDateStr}, nous vous tiendrons informé(e)`;
      if (lang === 'it') return `al più presto a partire da ${minDateStr}, La terremo informata`;
      if (lang === 'en') return `earliest from ${minDateStr}, we will keep you informed`;
      return `frühestens ab ${minDateStr}, Sie werden von uns informiert`;
    }
    if (lang === 'fr') return `entre le ${minDateStr} et le ${maxDateStr}`;
    if (lang === 'it') return `tra ${minDateStr} e ${maxDateStr}`;
    if (lang === 'en') return `between ${minDateStr} and ${maxDateStr}`;
    return `zwischen ${minDateStr} und ${maxDateStr}`;
  }

  function getSelectedQuantity(container) {
    if (!container) return 1;
    let scope = container.closest('form.js-product-form, form[action*="/cart/add"], product-form, .cc-main-product, .quickbuy-container, .section-main-product, [id^="shopify-section"]');
    if (!scope || !container.isConnected) {
      const productId = container.getAttribute('data-product-id');
      const liveContainer = productId
        ? document.querySelector(`[data-mi-delivery-eta][data-product-id="${productId}"]`)
        : document.querySelector('[data-mi-delivery-eta]');
      if (liveContainer && liveContainer !== container) {
        scope = liveContainer.closest('form.js-product-form, form[action*="/cart/add"], product-form, .cc-main-product, .quickbuy-container, .section-main-product, [id^="shopify-section"]');
      }
    }
    if (!scope) return 1;
    const qtyInput = scope.querySelector('input[name="quantity"]');
    if (!qtyInput) return 1;
    const val = parseInt(qtyInput.value, 10);
    return (!isNaN(val) && val >= 1) ? val : 1;
  }

  function renderDeliveryContainer(container, overrideVariantId) {
    if (!container) return;

    const langRaw = (container.getAttribute('data-locale') || 'de').slice(0, 2);
    const lang = ['de', 'fr', 'it', 'en'].includes(langRaw) ? langRaw : 'de';

    let variantDataMap = {};
    const scriptEl = container.querySelector('.js-variant-delivery-data');
    if (scriptEl) {
      try {
        variantDataMap = JSON.parse(scriptEl.textContent);
      } catch (e) {}
    }

    const activeVariantId = String(overrideVariantId || container.getAttribute('data-current-variant-id') || '');
    const vInfo = variantDataMap[activeVariantId];
    const isAvailable = vInfo ? (vInfo.available !== false) : true;

    const boxEl = container.querySelector('[data-mi-delivery-box]') || container.querySelector('.mi-delivery-eta__box');
    const propInput = container.querySelector('[data-mi-delivery-prop]');

    if (boxEl && vInfo) {
      boxEl.hidden = !isAvailable;
    }

    const indicatorEl = container.querySelector('[data-mi-delivery-indicator]');
    const textEl = container.querySelector('[data-mi-delivery-text]');
    if (!textEl) return;

    const isPreorder = container.getAttribute('data-is-preorder') === 'true';
    if (isPreorder) {
      if (boxEl) {
        boxEl.classList.remove('mi-delivery-eta__box--fast');
      }
      if (indicatorEl) {
        indicatorEl.textContent = '';
        indicatorEl.className = 'mi-delivery-eta__indicator mi-delivery-eta__indicator--medium';
      }
      const preorderLabelMap = {
        de: 'Vorbestellung',
        fr: 'Précommande',
        it: 'Preordine',
        en: 'Pre-order'
      };
      const preorderLabel = preorderLabelMap[lang] || 'Vorbestellung';
      const rawPreorderText = (container.getAttribute('data-preorder-text') || '').trim();
      const localizedPreorderText = localizePreorderText(rawPreorderText, lang);
      if (localizedPreorderText) {
        textEl.innerHTML = `<strong>${preorderLabel}</strong> – ${escapeHtml(localizedPreorderText)}`;
      } else {
        textEl.innerHTML = `<strong>${preorderLabel}</strong>`;
      }
      if (propInput) {
        if (!isAvailable) {
          propInput.disabled = true;
          propInput.value = '';
        } else {
          propInput.disabled = false;
          propInput.value = preorderLabel;
        }
      }
      return;
    }

    const selectedQty = getSelectedQuantity(container);
    const inv = (vInfo && typeof vInfo.inv === 'number')
      ? vInfo.inv
      : parseInt(container.getAttribute('data-current-inv') || '0', 10);
    const policy = (vInfo && vInfo.policy)
      ? vInfo.policy
      : (container.getAttribute('data-current-policy') || 'deny');
    const baseLf = (vInfo && vInfo.lf)
      ? vInfo.lf.trim()
      : (container.getAttribute('data-current-lf') || '5-7 Arbeitstage').trim();
    const fallbackLf = (vInfo && vInfo.fallbackLf)
      ? vInfo.fallbackLf.trim()
      : (container.getAttribute('data-current-fallback-lf') || baseLf).trim();

    const exceedsStock = inv > 0 && policy === 'continue' && selectedQty > inv;
    const rawLf = (exceedsStock && fallbackLf) ? fallbackLf : baseLf;
    const tierKey = resolveTierKey(rawLf);
    const tier = TIERS[tierKey] || TIERS['5-7 Arbeitstage'];

    const vacState = isVacationActive(container);
    const nowParts = getZurichNowParts();
    const todayUtc = new Date(Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day, 12, 0, 0));

    let minDateStr = '';
    let maxDateStr = '';

    if (vacState.active) {
      document.documentElement.classList.add('mi-vacation-mode-active');
      if (boxEl) {
        boxEl.classList.remove('mi-delivery-eta__box--fast');
      }
      if (indicatorEl) {
        indicatorEl.className = 'mi-delivery-eta__indicator mi-delivery-eta__indicator--vacation';
        const emoji = (container.getAttribute('data-vac-emoji') || '🏖️').trim();
        indicatorEl.textContent = emoji;
      }

      const vacName = container.getAttribute(`data-vac-name-${lang}`) || container.getAttribute('data-vac-name-de') || 'Betriebsferien';
      const shipDateStr = formatHumanDate(vacState.shipDate, lang);
      if (tier.onRequest) {
        minDateStr = formatHumanDate(addCalendarDaysToNextWorkingDay(vacState.shipDate, tier.calendarDays || 28), lang);
        maxDateStr = minDateStr;
      } else {
        minDateStr = formatHumanDate(addWorkingDays(vacState.shipDate, tier.minDays), lang);
        maxDateStr = formatHumanDate(addWorkingDays(vacState.shipDate, tier.maxDays), lang);
      }
      const shortDur = tier.short[lang] || tier.short.de;

      let html = '';
      if (tier.onRequest) {
        if (lang === 'fr') {
          html = `Traitement des commandes après nos <strong>${vacName}</strong> à partir du <strong>${shipDateStr}</strong>, <strong>délai de livraison au plus tôt à partir du ${minDateStr}</strong>, nous vous informerons après réception de la commande.`;
        } else if (lang === 'it') {
          html = `Elaborazione degli ordini dopo le nostre <strong>${vacName}</strong> a partire da <strong>${shipDateStr}</strong>, <strong>consegna al più presto a partire da ${minDateStr}</strong>, La informeremo dopo la ricezione dell'ordine.`;
        } else if (lang === 'en') {
          html = `Order processing after our <strong>${vacName}</strong> from <strong>${shipDateStr}</strong>, <strong>delivery earliest from ${minDateStr}</strong>, we will inform you after receipt of order.`;
        } else {
          html = `Bestellbearbeitung nach unseren <strong>${vacName}</strong> ab <strong>${shipDateStr}</strong>, <strong>Lieferfrist frühestens ab dem ${minDateStr}</strong>, wir informieren Sie nach Bestellungseingang.`;
        }
      } else if (tier.openEnded) {
        if (lang === 'fr') {
          html = `Traitement des commandes après nos <strong>${vacName}</strong> à partir du <strong>${shipDateStr}</strong> (+ ${shortDur}), livraison au plus tôt à partir du <strong>${minDateStr}</strong>, nous vous tiendrons informé(e).`;
        } else if (lang === 'it') {
          html = `Elaborazione degli ordini dopo le nostre <strong>${vacName}</strong> a partire da <strong>${shipDateStr}</strong> (+ ${shortDur}), consegna al più presto a partire da <strong>${minDateStr}</strong>, La terremo informata.`;
        } else if (lang === 'en') {
          html = `Order processing after our <strong>${vacName}</strong> from <strong>${shipDateStr}</strong> (+ ${shortDur}), delivery earliest from <strong>${minDateStr}</strong>, we will keep you informed.`;
        } else {
          html = `Bestellbearbeitung nach unseren <strong>${vacName}</strong> ab <strong>${shipDateStr}</strong> (+ ${shortDur}), Lieferung frühestens ab <strong>${minDateStr}</strong>, Sie werden von uns informiert.`;
        }
      } else {
        if (lang === 'fr') {
          html = `Traitement des commandes après nos <strong>${vacName}</strong> à partir du <strong>${shipDateStr}</strong> (+ ${shortDur}), livraison entre le <strong>${minDateStr}</strong> et le <strong>${maxDateStr}</strong>`;
        } else if (lang === 'it') {
          html = `Elaborazione degli ordini dopo le nostre <strong>${vacName}</strong> a partire da <strong>${shipDateStr}</strong> (+ ${shortDur}), consegna tra <strong>${minDateStr}</strong> e <strong>${maxDateStr}</strong>`;
        } else if (lang === 'en') {
          html = `Order processing after our <strong>${vacName}</strong> from <strong>${shipDateStr}</strong> (+ ${shortDur}), delivery between <strong>${minDateStr}</strong> and <strong>${maxDateStr}</strong>`;
        } else {
          html = `Bestellbearbeitung nach unseren <strong>${vacName}</strong> ab <strong>${shipDateStr}</strong> (+ ${shortDur}), Lieferung zwischen <strong>${minDateStr}</strong> und <strong>${maxDateStr}</strong>`;
        }
      }
      textEl.innerHTML = html;
    } else {
      document.documentElement.classList.remove('mi-vacation-mode-active');
      if (boxEl) {
        boxEl.classList.toggle('mi-delivery-eta__box--fast', tier.status === 'fast');
      }
      if (indicatorEl) {
        indicatorEl.textContent = '';
        indicatorEl.className = `mi-delivery-eta__indicator mi-delivery-eta__indicator--${tier.status}`;
      }

      if (tier.onRequest) {
        minDateStr = formatHumanDate(addCalendarDaysToNextWorkingDay(todayUtc, tier.calendarDays || 28), lang);
        maxDateStr = minDateStr;
      } else {
        minDateStr = formatHumanDate(addWorkingDays(todayUtc, tier.minDays), lang);
        maxDateStr = formatHumanDate(addWorkingDays(todayUtc, tier.maxDays), lang);
      }
      const mainLabel = tier.label[lang] || tier.label.de;

      let html = '';
      if (tier.onRequest) {
        if (lang === 'fr') {
          html = `<strong>Délai de livraison au plus tôt à partir du ${minDateStr}</strong>, nous vous informerons après réception de la commande.`;
        } else if (lang === 'it') {
          html = `<strong>Consegna al più presto a partire da ${minDateStr}</strong>, La informeremo dopo la ricezione dell'ordine.`;
        } else if (lang === 'en') {
          html = `<strong>Delivery earliest from ${minDateStr}</strong>, we will inform you after receipt of order.`;
        } else {
          html = `<strong>Lieferfrist frühestens ab dem ${minDateStr}</strong>, wir informieren Sie nach Bestellungseingang.`;
        }
      } else if (tier.openEnded) {
        if (lang === 'fr') {
          html = `<strong>${mainLabel}</strong> – Livraison au plus tôt à partir du <strong>${minDateStr}</strong>, nous vous tiendrons informé(e).`;
        } else if (lang === 'it') {
          html = `<strong>${mainLabel}</strong> – Consegna al più presto a partire da <strong>${minDateStr}</strong>, La terremo informata.`;
        } else if (lang === 'en') {
          html = `<strong>${mainLabel}</strong> – Delivery earliest from <strong>${minDateStr}</strong>, we will keep you informed.`;
        } else {
          html = `<strong>${mainLabel}</strong> – Lieferung frühestens ab <strong>${minDateStr}</strong>, Sie werden von uns informiert.`;
        }
      } else {
        if (lang === 'fr') {
          html = `<strong>${mainLabel}</strong> – Livraison entre le <strong>${minDateStr}</strong> et le <strong>${maxDateStr}</strong>`;
        } else if (lang === 'it') {
          html = `<strong>${mainLabel}</strong> – Consegna tra <strong>${minDateStr}</strong> e <strong>${maxDateStr}</strong>`;
        } else if (lang === 'en') {
          html = `<strong>${mainLabel}</strong> – Delivery between <strong>${minDateStr}</strong> and <strong>${maxDateStr}</strong>`;
        } else {
          html = `<strong>${mainLabel}</strong> – Lieferung zwischen <strong>${minDateStr}</strong> und <strong>${maxDateStr}</strong>`;
        }
      }
      textEl.innerHTML = html;
    }

    if (propInput) {
      if (!isAvailable) {
        propInput.disabled = true;
        propInput.value = '';
      } else {
        propInput.disabled = false;
        propInput.value = formatLineItemPropertyValue(minDateStr, maxDateStr, lang, !!tier.openEnded, !!tier.onRequest);
      }
    }
  }

  function findSelectedVariantId(sectionEl, container) {
    if (!sectionEl) return null;
    const formInput = sectionEl.querySelector('form.product-purchase-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
    if (formInput && formInput.value) {
      return formInput.value;
    }
    return container ? container.getAttribute('data-current-variant-id') : null;
  }

  function findVariantIdByOptions(sectionEl, container) {
    if (!sectionEl || !container) return null;
    const scriptEl = container.querySelector('.js-variant-delivery-data');
    if (!scriptEl) return null;
    let map = {};
    try { map = JSON.parse(scriptEl.textContent); } catch (e) { return null; }

    const picker = sectionEl.querySelector('variant-picker');
    if (!picker) return null;
    const selectedOpts = [];
    picker.querySelectorAll('.option-selector:not(.option-selector--custom)').forEach(sel => {
      const checked = sel.querySelector('input[type="radio"]:checked');
      if (checked) {
        selectedOpts.push(checked.value);
      } else {
        const select = sel.querySelector('select');
        if (select) selectedOpts.push(select.value);
      }
    });
    if (selectedOpts.length === 0) return null;

    for (const [vid, data] of Object.entries(map)) {
      const m1 = !selectedOpts[0] || data.opt1 === selectedOpts[0];
      const m2 = !selectedOpts[1] || data.opt2 === selectedOpts[1];
      const m3 = !selectedOpts[2] || data.opt3 === selectedOpts[2];
      if (m1 && m2 && m3) return vid;
    }
    return null;
  }

  function initAllContainers() {
    document.querySelectorAll('[data-mi-delivery-eta]').forEach(container => {
      renderDeliveryContainer(container);
    });
    checkCollectionVacationState();
  }

  function checkCollectionVacationState() {
    const marker = document.querySelector('[data-mi-vac-check]');
    if (!marker) return;
    const vacState = isVacationActive(marker);
    if (vacState.active) {
      document.documentElement.classList.add('mi-vacation-mode-active');
    } else {
      document.documentElement.classList.remove('mi-vacation-mode-active');
    }
  }

  document.addEventListener('change', (e) => {
    const sectionEl = e.target.closest('.cc-main-product, .quickbuy-container, .section-main-product, [id^="shopify-section"]');
    if (!sectionEl) return;
    const container = sectionEl.querySelector('[data-mi-delivery-eta]');
    if (!container) return;

    const explicitVid = e.detail && e.detail.variantId ? String(e.detail.variantId) : null;
    const optVid = findVariantIdByOptions(sectionEl, container);
    const fallbackVid = findSelectedVariantId(sectionEl, container);
    const targetVid = explicitVid || optVid || fallbackVid;

    if (targetVid) {
      container.setAttribute('data-current-variant-id', targetVid);
      renderDeliveryContainer(container, targetVid);
    }
  });

  document.addEventListener('input', (e) => {
    if (!e.target || !e.target.matches || !e.target.matches('input[name="quantity"]')) return;
    const sectionEl = e.target.closest('.cc-main-product, .quickbuy-container, .section-main-product, [id^="shopify-section"]');
    if (!sectionEl) return;
    const container = sectionEl.querySelector('[data-mi-delivery-eta]');
    if (!container) return;
    renderDeliveryContainer(container);
  });

  document.addEventListener('on:variant:change', (e) => {
    const sectionEl = e.target.closest('.cc-main-product, .quickbuy-container, .section-main-product, [id^="shopify-section"]') || document;
    const container = sectionEl.querySelector('[data-mi-delivery-eta]');
    if (container && e.detail && e.detail.variant) {
      const vid = String(e.detail.variant.id);
      container.setAttribute('data-current-variant-id', vid);
      renderDeliveryContainer(container, vid);
    }
  });

  document.addEventListener('on:variant:before-replace-element', (e) => {
    const replaceWith = e.detail && e.detail.replaceWith;
    if (!replaceWith || !replaceWith.querySelector) return;
    if (replaceWith.matches && replaceWith.matches('[data-mi-delivery-eta]')) {
      renderDeliveryContainer(replaceWith);
    } else if (replaceWith.querySelectorAll) {
      replaceWith.querySelectorAll('[data-mi-delivery-eta]').forEach(c => renderDeliveryContainer(c));
    }
  });

  // Ensure line item property is synced right before any form submission
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form && form.querySelector) {
      const container = form.querySelector('[data-mi-delivery-eta]');
      if (container) {
        const idInput = form.querySelector('input[name="id"]');
        renderDeliveryContainer(container, idInput && idInput.value ? idInput.value : undefined);
      }
    }
  }, true);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) {
          if (node.matches && node.matches('[data-mi-delivery-eta]')) {
            renderDeliveryContainer(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('[data-mi-delivery-eta]').forEach(c => renderDeliveryContainer(c));
          }
        }
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllContainers();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    initAllContainers();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
